import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { useState } from "react";

// Assets
import bgVideo from "../assets/video.webm";
import service1 from "../assets/service1.webp";
import service2 from "../assets/service2.webp";
import service3 from "../assets/service3.webp";
import aboutus from "../assets/aboutus.webp";
import main1 from "../assets/main1.webp";
import main2 from "../assets/main2.webp";
import main3 from "../assets/mainteam1.webp";
import main4 from "../assets/mainteam2.webp";

// ✅ Tambahkan 4 gambar artikel (buat file ini di src/assets)
// Kalau belum punya, sementara ganti pakai service1/service2/service3/aboutus
import articleImg1 from "../assets/article1.webp";
import articleImg2 from "../assets/article2.webp";
import articleImg3 from "../assets/article3.webp";
import articleImg4 from "../assets/article4.webp";

export default function Home() {
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  const clients = [
    { logo: "/assets/BASF-Logo.png", name: "BASF" },
    { logo: "/assets/Logo_Areva.svg", name: "Areva" },
    { logo: "/assets/Technip.svg", name: "Technip" },
    { logo: "/assets/Samsung_Logo.svg", name: "Samsung" },
  ];

  const mainTeamPhotos = [main1, main2, main3, main4];

  // ✅ Artikel: aku pertahankan isi kamu, tapi aku pecah "sources" biar link bisa biru & clickable.
  const articles = [
    {
      title:
        "The Role of VPS in Supporting Modern Enterprise Digital Infrastructure",
      tag: "VPS Infrastructure",
      desc:
        "Virtual Private Servers (VPS) play a critical role in modern enterprise digital infrastructure by providing isolated and configurable computing resources.",
      image: articleImg1,
      content: `
Virtual Private Servers (VPS) have become a critical part of modern digital infrastructure due to their ability to provide configurable and isolated computing resources without requiring full physical server ownership.

VPS solutions are widely used to host applications, websites, APIs, and internal enterprise systems. Each VPS instance operates with its own operating system and allocated resources, offering greater control and stability compared to shared hosting.

According to Hostinger, the global VPS market is projected to reach approximately USD 6.4 billion by 2026, with a CAGR of around 11.9%. Currently, more than 24 million websites worldwide are hosted on VPS environments.

These figures highlight the growing adoption of VPS solutions by enterprises seeking flexible, secure, and cost-efficient digital infrastructure.
      `,
      sources: [
        {
          label: "Wikipedia – Virtual Private Server",
          url: "https://en.wikipedia.org/wiki/Virtual_private_server",
        },
        {
          label: "Hostinger – Web Hosting Statistics",
          url: "https://www.hostinger.com/ph/tutorials/web-hosting-statistics",
        },
      ],
    },

    {
      title: "Global VPS Market Growth and Industry Trends",
      tag: "Market Insights",
      desc:
        "The global VPS market continues to grow rapidly, driven by digital transformation and demand for scalable infrastructure.",
      image: articleImg2,
      content: `
The global VPS market continues to experience significant growth, driven by increasing digital transformation across industries.

A report by Market Research Intellect estimates that the global VPS hosting market reached USD 11.69 billion in 2025 and is expected to grow at a CAGR of approximately 10.9% through 2033.

Similarly, IMARC Group projects that the global VPS market will reach USD 14.1 billion by 2033, with a CAGR of around 11.9%.

These data points demonstrate that VPS services are aligned with strong and sustained global market demand.
      `,
      sources: [
        {
          label: "Market Research Intellect (via LinkedIn)",
          url: "https://www.linkedin.com/pulse/virtual-private-servers-vps-hosting-szjzf/",
        },
        {
          label: "IMARC Group – VPS Market Report",
          url: "https://www.imarcgroup.com/virtual-private-server-market",
        },
      ],
    },

    {
      title: "VPS Security and Scalability for Modern Businesses",
      tag: "Security & Scalability",
      desc:
        "VPS solutions offer enhanced security and scalability, making them ideal for modern business environments.",
      image: articleImg3,
      content: `
One of the primary reasons organizations adopt VPS solutions is the combination of enhanced security and scalability.

VPS environments are isolated at the virtualization layer, significantly reducing the risk of interference between users compared to shared hosting platforms.

In real-world business applications, VPS is commonly used for testing, staging, internal APIs, and backend systems.

However, industry reports also warn that improperly managed VPS environments can pose security risks. TechRadar reported incidents where unsecured VPS servers were hijacked and used as malware proxy servers.

This highlights the importance of proper configuration, patching, firewall setup, and access control.
      `,
      sources: [
        {
          label: "Wikipedia – Virtual Private Server",
          url: "https://en.wikipedia.org/wiki/Virtual_private_server",
        },
        {
          label: "TechRadar – VPS Security Risks",
          url: "https://www.techradar.com/pro/security/vps-servers-hijacked-into-malware-proxies-heres-how-to-stay-safe",
        },
      ],
    },

    {
      title: "VPS as a Foundation for Cloud and Artificial Intelligence Technologies",
      tag: "Cloud & AI",
      desc:
        "VPS plays an important role as a foundation for cloud computing and early-stage AI development.",
      image: articleImg4,
      content: `
Beyond traditional hosting, VPS plays a vital role in modern cloud architectures and early-stage Artificial Intelligence (AI) deployments.

Many organizations use VPS environments as development and testing platforms for AI, machine learning, and data-processing workloads before scaling to hyperscale cloud platforms.

This approach supports hybrid cloud strategies, where VPS, cloud VPS, and hyperscale cloud services are combined to meet different operational and performance requirements.

VPS provides flexibility, cost efficiency, and rapid deployment capabilities, making it a strategic foundation for modern digital transformation initiatives.
      `,
      sources: [
        {
          label: "AWS – What is VPS?",
          url: "https://aws.amazon.com/what-is/vps/",
        },
      ],
    },
  ];

  const [selectedArticle, setSelectedArticle] = useState(null);

  return (
    <div className="flex flex-col">
      {/* ================= HERO SECTION ================= */}
      <section className="relative w-screen h-[80vh] sm:h-screen overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute top-0 left-0 w-full h-full object-cover"
        >
          <source src={bgVideo} type="video/webm" />
        </video>
        <div className="absolute top-0 left-0 w-full h-full bg-black/40" />

        <div className="relative z-10 flex flex-col justify-center items-center h-full text-center text-white px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight">
            Welcome to Inovasi Global Mumpuni
          </h1>
          <p className="text-base sm:text-lg md:text-xl mb-6 max-w-2xl">
            We provide reliable and sustainable solutions across industries from
            technology, chemicals, environtment and supply chain.
          </p>
          <Link
            to="/service"
            className="px-5 py-3 bg-white text-blue-600 rounded-xl font-semibold shadow-lg hover:scale-105 transition"
          >
            Explore Services
          </Link>
        </div>
      </section>

      {/* ================= SERVICE SECTION ================= */}
      <section className="py-16 sm:py-20 bg-gray-100 dark:bg-gray-900 text-center px-4 sm:px-6 md:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-300 mb-6">
          Our Services
        </h2>

        <p className="max-w-3xl mx-auto text-gray-700 dark:text-gray-300 mb-12 text-sm sm:text-base">
          Delivering technology consulting, trusted global supply chains, and
          strategies for sustainable growth empowering businesses for today and
          the future.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            {
              img: service1,
              title: "Global Technology Consulting",
              desc:
                "Providing modern technology solutions and strategic consulting to support digital transformation.",
            },
            {
              img: service2,
              title: "Strategic Supply Chain",
              desc:
                "Building a trusted global supply and vendor to ensure smooth business operations.",
            },
            {
              img: service3,
              title: "Digital Infrastructure Provisioning",
              desc:
                "Providing resilient, scalable, and performance driven digital infrastructure tailored to support diverse applications, workload demands, and longterm business growth.",
            },
          ].map((service, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="
                bg-white 
                dark:bg-white/10 
                dark:backdrop-blur-md 
                dark:border dark:border-white/10
                rounded-xl shadow-md 
                overflow-hidden hover:shadow-xl 
                transition flex flex-col
              "
            >
              <img
                src={service.img}
                alt={service.title}
                className="w-full h-48 sm:h-56 md:h-64 object-cover"
              />

              <div className="p-6 flex flex-col flex-grow text-left">
                <h3 className="text-lg sm:text-xl font-bold mb-2 dark:text-white">
                  {service.title}
                </h3>

                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
                  {service.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12">
          <Link
            to="/service"
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold shadow-md hover:scale-105 transition"
          >
            Explore More
          </Link>
        </div>
      </section>

      {/* ================= ABOUT US SECTION ================= */}
      <section className="py-16 sm:py-20 bg-white dark:bg-gray-900 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.img
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            src={aboutus}
            alt="About Inovasi Global Mumpuni"
            loading="lazy"
            className="rounded-2xl shadow-lg object-cover w-full h-64 sm:h-80 md:h-96"
          />

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-300 mb-6">
              What Is PT. Inovasi Global Mumpuni
            </h2>

            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-sm sm:text-base">
              PT. Inovasi Global Mumpuni is a trusted partner in delivering
              innovative and sustainable solutions across industries. Our
              expertise spans{" "}
              <span className="font-semibold">Global Technology Consulting</span>
              , <span className="font-semibold">Strategic Supply Chains</span>,
              and{" "}
              <span className="font-semibold">
                Sustainable Business Growth
              </span>
              .
            </p>

            <div className="grid gap-6 sm:grid-cols-2">
              {[
                {
                  title: "Our Vision",
                  desc:
                    "To become a global leader in innovative, sustainable, and technology-driven solutions.",
                  color: "text-blue-600 dark:text-blue-300",
                },
                {
                  title: "Our Mission",
                  desc:
                    "To deliver value through advanced consulting, reliable supply chains, and sustainable growth strategies.",
                  color: "text-green-600 dark:text-green-300",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                  className="
                    bg-gray-50 
                    dark:bg-white/10 
                    dark:backdrop-blur-md 
                    dark:border dark:border-white/10 
                    p-6 rounded-xl shadow 
                    hover:shadow-lg transition
                  "
                >
                  <h3
                    className={`text-lg sm:text-xl font-bold mb-3 ${item.color}`}
                  >
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= ARTICLE SECTION ================= */}
      <section className="py-16 sm:py-20 bg-gray-50 dark:bg-gray-900 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-300 mb-4"
          >
            Technology Insights
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-gray-700 dark:text-gray-300 mb-10 text-sm sm:text-base"
          >
            Explore the latest insights on technology, innovation, and solutions
            we deliver to support sustainable business growth.
          </motion.p>

          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={{ clickable: true }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="pb-10"
          >
            {articles.map((article, index) => (
              <SwiperSlide key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                  className="
                    h-full
                    bg-white
                    dark:bg-white/10
                    dark:backdrop-blur-md
                    dark:border dark:border-white/10
                    rounded-2xl
                    p-6
                    shadow-md
                    hover:shadow-xl
                    transition
                    flex flex-col
                    text-left
                  "
                >
                  {/* ✅ IMAGE OUTSIDE MODAL (HEIGHT FIXED => CARD HEIGHT SAME) */}
                  <div className="w-full h-40 mb-4 overflow-hidden rounded-xl">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  <span className="text-xs font-semibold text-blue-600 dark:text-blue-300 mb-2">
                    {article.tag}
                  </span>

                  <h3 className="text-lg sm:text-xl font-bold mb-3 dark:text-white line-clamp-2 min-h-[56px]">
                    {article.title}
                  </h3>

                  <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base line-clamp-3 min-h-[72px]">
                    {article.desc}
                  </p>

                  <button
                    onClick={() => setSelectedArticle(article)}
                    className="
                      mt-auto
                      inline-flex items-center justify-center
                      px-4 py-2
                      border border-blue-600
                      text-blue-600
                      dark:border-blue-300 dark:text-blue-300
                      rounded-lg
                      font-semibold text-sm
                      transition
                      hover:bg-blue-600 hover:text-white
                      dark:hover:bg-blue-300 dark:hover:text-gray-900
                    "
                  >
                    View More
                  </button>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* ================= TEAM SECTION ================= */}
      <section className="py-16 sm:py-20 bg-white dark:bg-gray-900 text-center px-4 sm:px-6 relative z-10">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="overflow-hidden rounded-2xl shadow-lg"
          >
            <Swiper
              modules={[Pagination, Autoplay, EffectFade]}
              effect="fade"
              pagination={{ clickable: true }}
              autoplay={{ delay: 2500, disableOnInteraction: false }}
              slidesPerView={1}
              className="h-64 sm:h-80"
            >
              {mainTeamPhotos.map((photo, index) => (
                <SwiperSlide key={index}>
                  <img
                    src={photo}
                    alt={`Main Team ${index + 1}`}
                    className="rounded-2xl object-cover w-full h-full"
                    loading="lazy"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-left mt-8 md:mt-0"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-300 mb-4">
              Meet Our Team
            </h2>

            <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed text-sm sm:text-base">
              Our passionate professionals collaborate with integrity and
              creativity to deliver innovative and sustainable solutions.
            </p>

            <Link
              to="/team"
              className="px-5 py-3 bg-blue-600 text-white rounded-xl font-semibold shadow-md hover:scale-105 transition"
            >
              Learn More
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ================= CLIENT SECTION ================= */}
      <section className="w-full py-16 bg-blue-50 dark:bg-blue-950">
        <div className="max-w-6xl mx-auto text-center px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-white mb-4">
            Our Clients
          </h2>

          <p className="text-gray-800 dark:text-white mb-10">
            Trusted by leading companies and organizations across multiple
            industries.
          </p>

          <div className="flex flex-wrap justify-center items-center gap-10">
            {clients.map((c, idx) => (
              <div
                key={idx}
                className="
                  bg-white
                  p-5 rounded-xl
                  shadow-md
                  transition-all duration-300
                  hover:shadow-xl
                  hover:scale-105
                "
              >
                <img src={c.logo} alt={c.name} className="h-14 object-contain" />
              </div>
            ))}
          </div>

          <Link
            to="/client"
            className="
              inline-block
              mt-10 px-6 py-3
              bg-blue-600 text-white rounded-lg
              border border-transparent
              transition-all duration-300
              hover:bg-white hover:text-blue-600 hover:border-blue-600
              hover:scale-105 hover:shadow-lg
            "
          >
            See All Clients
          </Link>
        </div>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section
        className="
          py-16 sm:py-20 px-4 text-center
          bg-gray-100 dark:bg-gray-900
          transition-colors duration-300
        "
      >
        <h2 className="text-2xl sm:text-3xl text-blue-600 dark:text-blue-300 font-bold mb-6">
          Ready to collaborate with us?
        </h2>

        <p className="max-w-2xl mx-auto mb-6 text-sm sm:text-base text-gray-700 dark:text-gray-300">
          Let’s work together to create innovative and sustainable solutions.
        </p>

        <Link
          to="/contact"
          className="
            inline-flex items-center gap-2
            px-6 py-3
            bg-blue-600 text-white
            rounded-xl font-semibold shadow-md
            hover:scale-105 transition
          "
        >
          ✉️ Contact Us
        </Link>
      </section>

      {/* ================= MODAL ================= */}
      {selectedArticle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          onClick={() => setSelectedArticle(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 max-w-2xl w-full rounded-2xl p-6 max-h-[80vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedArticle(null)}
              className="absolute top-4 right-4 text-gray-500 hover:text-red-500 text-xl"
            >
              ✕
            </button>

            {/* ✅ IMAGE INSIDE MODAL */}
            <img
              src={selectedArticle.image}
              alt={selectedArticle.title}
              className="w-full h-56 object-cover rounded-xl mb-4"
              loading="lazy"
            />

            <span className="text-xs font-semibold text-blue-600 dark:text-blue-300">
              {selectedArticle.tag}
            </span>

            <h2 className="text-2xl font-bold mt-2 mb-4 dark:text-white">
              {selectedArticle.title}
            </h2>

            {/* ✅ CONTENT */}
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed text-sm sm:text-base">
              {selectedArticle.content}
            </p>

            {/* ✅ SOURCES (LINK BIRU & CLICKABLE) */}
            {selectedArticle.sources?.length > 0 && (
              <div className="mt-6 border-t border-white/10 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Sources
                </h3>

                <ul className="space-y-2">
                  {selectedArticle.sources.map((source, index) => (
                    <li key={index}>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-300 hover:underline text-sm"
                      >
                        {source.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
